import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, X, ChevronLeft, ChevronRight, Building2, User, KeyRound,
  FileText, Wrench, CreditCard, ClipboardCheck, ExternalLink,
} from 'lucide-react';
import { theme } from '../shared/theme';
import { portalFetch } from '../shared/api';
import { pollWhileActive } from '../shared/messaging';
import { useLanguage } from '../../components/LanguageContext';
import AdminPmDashboard, { isRented, isOffplan } from './AdminPmDashboard';
import '../shared/portals.css';

// ============================================================
// AdminProperties — Property Management, inside the app.
//
// This is the console's own ground, rebuilt natively rather than framed.
// Framing the existing page is refused by the browser (see ConsoleFrame) and
// the fix for that is not ours to make, so the screens move here instead,
// which is where they were wanted in the first place.
//
// It reads /pm/all — one call that already returns every unit hydrated with
// its owner, current tenant, documents, maintenance, inspections and rent
// schedule. That endpoint has been live for months: nothing here needs a
// backend deploy, which is why this could ship on its own.
//
// READ, not write. Uploading a document, adding a unit, extracting an Oqood —
// those still belong to the full console, one tap away, because each is a
// form with real consequences and moving them needs its own care. What the
// team does all day is LOOK: who is in this flat, when does the rent land,
// what has been reported. That is what is here.
// ============================================================

// Five minutes. /pm/all is the heaviest call in the product — it hydrates
// every unit with four sub-queries each, so a hundred properties is several
// hundred reads and seconds of CPU per call. A tenancy does not change while
// you are looking at the list. pollWhileActive stops it after five idle
// minutes and refreshes the moment you come back, which is when it matters.
const POLL_MS = 300000;

interface Unit {
  id: string;
  name?: string;
  project?: string;
  community?: string;
  unitNumber?: string;
  status?: string;
  image?: string;
  ownerId?: string;
  ownerName?: string | null;
  currentTenant?: { id?: string; name?: string; email?: string; phone?: string; contractStart?: string; contractEnd?: string; ejariNo?: string } | null;
  documents?: { id: string; name?: string; documentType?: string; type?: string; date?: string; fileUrl?: string }[];
  maintenance?: { id: string; title?: string; category?: string; status?: string; cost?: number; createdAt?: any }[];
  inspectionReports?: { id: string; type?: string; date?: string; fileUrl?: string }[];
  rentSchedule?: { chequeNo?: string; amount?: number; dueDate?: string; status?: string; paid?: boolean }[];
  expenses?: { id: string; type?: string; description?: string; amount?: number; status?: string; date?: string }[];
}

const AED = (n: number) => `AED ${Math.round(n || 0).toLocaleString('en-AE')}`;

// `name` and `project` disagree on some records, so both are shown rather
// than one picked blind — never `name || project`, which silently swaps
// between two different labels for the same property.
const unitTitle = (u: Unit) => u.name || [u.project, u.unitNumber].filter(Boolean).join(' ') || u.id;
const unitSub = (u: Unit) => [u.project, u.unitNumber, u.community].filter(Boolean).join(' · ');

function statusTone(status?: string): { bg: string; fg: string } {
  const s = (status || '').toLowerCase();
  if (s.includes('rent') || s.includes('occupied')) return { bg: theme.successBg, fg: theme.success };
  if (s.includes('vacant')) return { bg: theme.warningBg, fg: theme.warning };
  if (s.includes('sold')) return { bg: '#f3f4f6', fg: theme.slate };
  return { bg: theme.infoBg, fg: theme.info };
}

const fmtDate = (d?: string) => {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// No props: everything this screen needs is its own. The one way out to the
// full console lives in the console header, once, rather than on every
// screen — see EAAdminConsole.
type Props = Record<string, never>;

const AdminProperties: React.FC<Props> = () => {
  const { t } = useLanguage();
  const [units, setUnits] = useState<Unit[]>([]);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [pane, setPane] = useState<'dashboard' | 'units'>('dashboard');
  // RENTED FIRST, and by default the only thing listed.
  //
  // The app's console is the RENTAL operation: the units with a tenant in
  // them are the ones that generate rent to collect, contracts to renew,
  // complaints to answer and messages to reply to. The other ninety-five are
  // pipeline — off-plan waiting on handover, vacant waiting on a tenant —
  // and that work belongs to the full console, which is built for it.
  //
  // The others stay one tap away rather than being hidden: "show me the
  // empty ones" is a real question, just not the one this screen opens on.
  const [kind, setKind] = useState<'rented' | 'vacant' | 'offplan' | 'all'>('rented');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (!opts.silent) setLoading(true);
    try {
      const res = await portalFetch<{ units: Unit[] }>('/pm/all');
      setUnits(res.units || []);
      setError(null);
    } catch (e: any) {
      if (!opts.silent) setError(e?.message || t('apLoadFailed'));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => pollWhileActive(() => load({ silent: true }), POLL_MS), [load]);

  const counts = useMemo(() => ({
    rented: units.filter(isRented as any).length,
    offplan: units.filter(isOffplan as any).length,
    vacant: units.filter((u) => !isRented(u as any) && !isOffplan(u as any)).length,
    all: units.length,
  }), [units]);

  const filtered = useMemo(() => {
    const byKind = kind === 'all' ? units
      : kind === 'rented' ? units.filter((u) => isRented(u as any))
        : kind === 'offplan' ? units.filter((u) => isOffplan(u as any))
          : units.filter((u) => !isRented(u as any) && !isOffplan(u as any));
    const needle = q.trim().toLowerCase();
    if (!needle) return byKind;
    return byKind.filter((u) =>
      `${unitTitle(u)} ${unitSub(u)} ${u.ownerName || ''} ${u.currentTenant?.name || ''} ${u.status || ''}`
        .toLowerCase().includes(needle));
  }, [units, q, kind]);

  const open = units.find((u) => u.id === openId) || null;
  if (open) return <UnitDetail unit={open} onBack={() => setOpenId(null)} />;

  return (
    <div className="pt-fade" style={{ padding: 14 }}>
      {/* Numbers first, list second — the console opens on the answer and
          this opened on 99 rows, which is the same data with the answer left
          out. The list is one tap behind it. */}
      <div style={{ display: 'flex', gap: 7, marginBottom: 12 }}>
        {([['dashboard', t('pdOverview')], ['units', t('apUnits')]] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setPane(k)}
            className="pt-tap"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: pane === k ? theme.navy : '#fff',
              color: pane === k ? '#fff' : theme.slate,
              border: `1px solid ${pane === k ? theme.navy : theme.line}`,
              borderRadius: 99, padding: '7px 14px', minHeight: 0,
              fontSize: 12.5, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <div className="pm-chat-error" style={{ margin: '0 0 10px' }}>{error}</div>}

      {pane === 'dashboard' && !loading && (
        <AdminPmDashboard units={units as any} onSeeUnits={() => setPane('units')} />
      )}
      {pane === 'dashboard' && loading && !units.length && (
        <>
          <div className="pt-skel" style={{ height: 96, marginBottom: 8 }} />
          <div className="pt-skel" style={{ height: 130 }} />
        </>
      )}

      {pane === 'units' && (
      <>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${theme.line}`, borderRadius: 12, padding: '9px 12px', marginBottom: 10 }}>
        <Search size={17} color={theme.slateLight} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('apSearch')}
          style={{ border: 'none', outline: 'none', fontSize: 14.5, flex: 1, background: 'transparent', padding: 0, width: 'auto' }}
        />
        {q && (
          <button onClick={() => setQ('')} aria-label={t('inbClear')} style={{ background: 'none', border: 'none', padding: 0, minHeight: 0, color: theme.slateLight }}>
            <X size={16} />
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 7, marginBottom: 10, flexWrap: 'wrap' }}>
        {([['rented', t('pdRented'), counts.rented],
           ['vacant', t('pdVacant'), counts.vacant],
           ['offplan', t('pdOffplan'), counts.offplan],
           ['all', t('inbFilterAll'), counts.all]] as const).map(([k, label, n]) => {
          const on = kind === k;
          return (
            <button
              key={k}
              onClick={() => setKind(k as typeof kind)}
              className="pt-tap"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: on ? theme.navy : '#fff',
                color: on ? '#fff' : theme.slate,
                border: `1px solid ${on ? theme.navy : theme.line}`,
                borderRadius: 99, padding: '7px 13px', minHeight: 0,
                fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {label} <span style={{ opacity: 0.75 }}>{n}</span>
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 4px 10px', color: theme.slate, fontSize: 12.5, fontWeight: 700 }}>
        <Building2 size={15} /> {filtered.length} {t('apUnits')}
      </div>

      {loading && !units.length && (
        <>
          <div className="pt-skel" style={{ height: 78, marginBottom: 8 }} />
          <div className="pt-skel" style={{ height: 78, marginBottom: 8 }} />
          <div className="pt-skel" style={{ height: 78 }} />
        </>
      )}

      {!loading && !filtered.length && (
        <div className="pm-chat-empty">
          <Building2 size={30} color={theme.gold} />
          <div className="pm-chat-empty-title">{t('apNoUnits')}</div>
          <div className="pm-chat-empty-text">{t('apNoUnitsText')}</div>
        </div>
      )}

      {filtered.map((u) => {
        const tone = statusTone(u.status);
        const tenant = u.currentTenant;
        const openJobs = (u.maintenance || []).filter((m) => (m.status || '').toLowerCase() !== 'resolved').length;
        return (
          <button key={u.id} onClick={() => setOpenId(u.id)} className="pt-tap pm-inbox-row" style={{ alignItems: 'center' }}>
            <div className="pm-inbox-avatar"><Building2 size={19} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="pm-inbox-name">{unitTitle(u)}</div>
              <div className="pm-inbox-preview">{unitSub(u) || '—'}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                <span className="pm-issue-status" style={{ background: tone.bg, color: tone.fg }}>{u.status || t('apStatusUnknown')}</span>
                {/* Who is in it, on the row — the question asked most often. */}
                <span className="pm-issue-status" style={{ background: tenant ? theme.infoBg : '#f3f4f6', color: tenant ? theme.info : theme.slate }}>
                  {tenant ? tenant.name : t('apVacant')}
                </span>
                {openJobs > 0 && (
                  <span className="pm-issue-status" style={{ background: theme.warningBg, color: theme.warning }}>
                    {openJobs} {t('apOpenJobs')}
                  </span>
                )}
              </div>
            </div>
            <ChevronRight size={20} color={theme.slateLight} />
          </button>
        );
      })}
      </>
      )}
    </div>
  );
};

// ---------------- one property ----------------

const UnitDetail: React.FC<{ unit: Unit; onBack: () => void }> = ({ unit, onBack }) => {
  const { t } = useLanguage();
  const tone = statusTone(unit.status);
  const tenant = unit.currentTenant;
  const rent = unit.rentSchedule || [];
  const collected = rent.filter((r) => r.paid || (r.status || '').toLowerCase() === 'paid')
    .reduce((n, r) => n + (Number(r.amount) || 0), 0);
  const total = rent.reduce((n, r) => n + (Number(r.amount) || 0), 0);

  return (
    <div className="pt-fade" style={{ padding: 14 }}>
      <button
        onClick={onBack}
        className="pt-tap"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: `1px solid ${theme.line}`, borderRadius: 10, padding: '7px 12px', minHeight: 0, fontSize: 12.5, fontWeight: 700, color: theme.slate, cursor: 'pointer', marginBottom: 12 }}
      >
        <ChevronLeft size={15} /> {t('apAllUnits')}
      </button>

      <div className="pt-card" style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: theme.ink }}>{unitTitle(unit)}</div>
        <div style={{ fontSize: 13, color: theme.slate, marginTop: 3 }}>{unitSub(unit) || '—'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 9 }}>
          <span className="pm-issue-status" style={{ background: tone.bg, color: tone.fg }}>{unit.status || t('apStatusUnknown')}</span>
          {unit.ownerName && (
            <span className="pm-issue-status" style={{ background: '#fdf6e6', color: '#8a6d1f' }}>
              {t('eaPropertyOwner')}: {unit.ownerName}
            </span>
          )}
        </div>
      </div>

      <Section icon={User} title={t('apTenant')}>
        {tenant ? (
          <>
            <Row label={t('apName')} value={tenant.name || '—'} />
            <Row label={t('apEmail')} value={tenant.email || '—'} />
            <Row label={t('apPhone')} value={tenant.phone || '—'} />
            <Row label={t('apContract')} value={`${fmtDate(tenant.contractStart)} → ${fmtDate(tenant.contractEnd)}`} />
            <Row label={t('apEjari')} value={tenant.ejariNo || '—'} />
          </>
        ) : (
          <Empty text={t('apVacantText')} />
        )}
      </Section>

      <Section icon={CreditCard} title={`${t('apRent')} · ${AED(collected)} / ${AED(total)}`}>
        {rent.length ? rent.map((r, i) => (
          <Row
            key={r.chequeNo || i}
            label={`${t('apCheque')} ${r.chequeNo || i + 1} · ${fmtDate(r.dueDate)}`}
            value={`${AED(Number(r.amount) || 0)} · ${(r.paid || (r.status || '').toLowerCase() === 'paid') ? t('apPaid') : t('apDue')}`}
          />
        )) : <Empty text={t('apNoRent')} />}
      </Section>

      <Section icon={Wrench} title={t('apMaintenance')}>
        {(unit.maintenance || []).length
          ? (unit.maintenance || []).map((m) => (
            <Row key={m.id} label={m.title || m.category || '—'} value={`${m.status || 'open'}${m.cost ? ` · ${AED(m.cost)}` : ''}`} />
          ))
          : <Empty text={t('apNoMaintenance')} />}
      </Section>

      <Section icon={FileText} title={t('documents')}>
        {(unit.documents || []).length
          ? (unit.documents || []).map((d) => (
            <Row key={d.id} label={d.name || d.documentType || d.type || '—'} value={fmtDate(d.date)} href={d.fileUrl} />
          ))
          : <Empty text={t('apNoDocuments')} />}
      </Section>

      <Section icon={ClipboardCheck} title={t('pmInspection')}>
        {(unit.inspectionReports || []).length
          ? (unit.inspectionReports || []).map((r) => (
            <Row key={r.id} label={r.type || '—'} value={fmtDate(r.date)} href={r.fileUrl} />
          ))
          : <Empty text={t('apNoInspections')} />}
      </Section>

      <Section icon={KeyRound} title={t('expenses')}>
        {(unit.expenses || []).length
          ? (unit.expenses || []).map((e) => (
            <Row key={e.id} label={`${e.type || ''} ${e.description || ''}`.trim() || '—'} value={`${AED(Number(e.amount) || 0)} · ${e.status || ''}`} />
          ))
          : <Empty text={t('apNoExpenses')} />}
      </Section>
    </div>
  );
};

const Section: React.FC<{ icon: React.ComponentType<any>; title: string; children: React.ReactNode }> = ({ icon: Icon, title, children }) => (
  <div className="pt-card" style={{ padding: 14, marginBottom: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
      <Icon size={16} color={theme.gold} />
      <span style={{ fontSize: 13.5, fontWeight: 800, color: theme.ink }}>{title}</span>
    </div>
    {children}
  </div>
);

const Row: React.FC<{ label: string; value: string; href?: string }> = ({ label, value, href }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderTop: `1px solid ${theme.line}` }}>
    <span style={{ fontSize: 13, color: theme.slate, minWidth: 0, overflowWrap: 'anywhere' }}>{label}</span>
    {href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 700, color: theme.info, textDecoration: 'none', whiteSpace: 'nowrap' }}>
        {value} ↗
      </a>
    ) : (
      <span style={{ fontSize: 13, fontWeight: 700, color: theme.ink, textAlign: 'right', overflowWrap: 'anywhere' }}>{value}</span>
    )}
  </div>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ fontSize: 12.5, color: theme.slateLight, paddingTop: 4 }}>{text}</div>
);

export default AdminProperties;
